import React, {
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import findLastIndex from 'lodash/findLastIndex';
import get from 'lodash/get';
import slice from 'lodash/slice';
import { Box } from '@atlaskit/primitives';
import { useIntl, type IntlShape } from '@atlassian/jira-intl';
import { IssueHierarchySkeleton } from '@atlassian/jira-portfolio-3-common';
import { monitor } from '@atlassian/jira-portfolio-3-common/src/analytics/performance';
import { SORTED_PREVENT_ISSUE_RANKING_WARNING } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/flags';
import {
	TABLE_GROUP,
	type TableRow,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/table';
import StickyHeader from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/sticky-header';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import { PROFILE_KEYS } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import { manuallyUpdateReactVirtualizedListWidth } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/magic';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
import { SortableIssue, SortableIssuesWithoutParent, SortableList } from './list';
import NoIssuesEmptyState from './no-issues-empty-state';
import NoIssuesInFilterView from './no-issues-in-filter-empty-view';
import SelectableIssue from './selectable-issue';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import TableRowComponent from './table-row';
import {
	type DropState,
	DROP_INVALID_SIBLING,
	DROP_NONE,
	type Props,
	type SortDelta,
	type SortOver,
} from './types';
import { isSortByRank, getNewDropState, isTableGroupItem } from './util';
import {
	componentDidUpdateHelper,
	disableUserSelection,
	getDraggedNode,
	getDropClassName,
	onSortEndHelper,
	shouldCancelStart,
} from './utils';

export type PropsWithIntl = Props & {
	intl: IntlShape;
};

export const initialDropState: DropState = {
	anchor: null,
	group: null,
	id: null,
	parentId: null,
	relation: null,
	type: DROP_NONE,
	errorType: null,
};

const IssuesHOC = (props: Omit<Props, 'listContainerEl'>) => {
	const oldWidth = useRef<number>(props.width);
	const listContainerElRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (props.isDragging && oldWidth.current !== props.width && listContainerElRef.current) {
			manuallyUpdateReactVirtualizedListWidth(listContainerElRef.current, props.width);
			oldWidth.current = props.width;
		}
	}, [props.isDragging, props.width]);

	return <Issues {...props} ref={listContainerElRef} />;
};

export const Issues = memo(
	forwardRef<HTMLDivElement, Props>(
		(
			{
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				onExperienceSuccess = () => {},
				isGroupingByMultiSelectCustomField = false,
				SelectableIssueComponent = SelectableIssue,
				SortableIssueComponent = SortableIssue,
				SortableIssuesWithoutParentComponent = SortableIssuesWithoutParent,
				StickyHeaderComponent = StickyHeader,
				sorting,
				issues,
				startLevel,
				isLoading,
				scrollToIndex,
				noIssuesInPlan,
				isReadOnly,
				rankDigits,
				grouping,
				warnings,
				showWarning,
				scrollElement,
				width,
				disableDnD,
				onToggle,
				onMove,
				onInvalidRanking,
			},
			ref,
		) => {
			const [drop, setDrop] = useState<DropState>(initialDropState);
			const intl = useIntl();

			const hasMounted = useRef<boolean>(false);
			const stickyHeaderIndex = useRef<number | undefined>(undefined);

			const onListContainerMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
				// Prevent text selection when we use shift key to select multiple issues
				if (e.ctrlKey || e.shiftKey) {
					e.preventDefault();
				}
			};

			useEffect(() => {
				onExperienceSuccess();
			}, [onExperienceSuccess]);

			// Simulates componentDidUpdate
			useLayoutEffect(() => {
				if (hasMounted.current) {
					componentDidUpdateHelper(drop, getDraggedNode, isLoading);
				} else {
					hasMounted.current = true;
				}
			});

			const onSortStart = useCallback(() => {
				disableUserSelection(true);

				const node = getDraggedNode();
				if (node) {
					node.classList.add(styles.isDragging);
				}
				if (!isSortByRank(sorting.field)) {
					onInvalidRanking({ key: SORTED_PREVENT_ISSUE_RANKING_WARNING });
					setDrop({ ...initialDropState, type: DROP_INVALID_SIBLING });
				}
			}, [onInvalidRanking, sorting.field]);

			const onSortOver = useCallback(
				(delta: SortOver) => {
					if (isSortByRank(sorting.field)) {
						setDrop(
							getNewDropState({
								items: issues,
								index: delta.index,
								newIndex: delta.newIndex,
								startLevel,
							}),
						);
					}
					// skip onSortOver logic if issues are sorted by non-rank field
				},
				[issues, sorting.field, startLevel],
			);

			const onSortEnd = useCallback(
				(delta: SortDelta) => {
					setDrop((currentDrop) =>
						onSortEndHelper(currentDrop, onMove, sorting, onInvalidRanking, issues, delta),
					);
					window.getSelection()?.empty();
				},
				[issues, onInvalidRanking, onMove, sorting],
			);

			const calculateHeightOffset = useCallback(() => {
				const nextStickyHeaderIndex = findLastIndex(
					slice(issues, 0, scrollToIndex || issues.length),
					(item) => get(item, 'tag') === TABLE_GROUP,
				);

				if (
					isDefined(stickyHeaderIndex.current) &&
					nextStickyHeaderIndex <= stickyHeaderIndex.current
				) {
					const issue = issues[nextStickyHeaderIndex];
					const nextStickyHeaderHeight = isTableGroupItem(issue) && issue.height ? issue.height : 0;
					return Number(nextStickyHeaderHeight) * 40;
				}
				return 0;
			}, [issues, scrollToIndex]);

			const rowRenderer = useCallback(
				({ key, index, style, isScrolling }: TableRow) => (
					<TableRowComponent
						key={key}
						index={index}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={style}
						isScrolling={isScrolling}
						drop={drop}
						startLevel={startLevel}
						isReadOnly={isReadOnly}
						rankDigits={rankDigits}
						issues={issues}
						grouping={grouping}
						warnings={warnings}
						showWarning={showWarning}
						onToggle={onToggle}
						SelectableIssueComponent={SelectableIssueComponent}
						SortableIssueComponent={SortableIssueComponent}
						SortableIssuesWithoutParentComponent={SortableIssuesWithoutParentComponent}
						isGroupingByMultiSelectCustomField={isGroupingByMultiSelectCustomField}
					/>
				),
				[
					SelectableIssueComponent,
					SortableIssueComponent,
					SortableIssuesWithoutParentComponent,
					drop,
					grouping,
					isGroupingByMultiSelectCustomField,
					isReadOnly,
					issues,
					onToggle,
					rankDigits,
					showWarning,
					startLevel,
					warnings,
				],
			);

			const { parentId } = drop;
			if (!isLoading) {
				monitor.startProfile(PROFILE_KEYS.RENDER.SCOPE_TABLE);
			}

			const getScrollContainer = useCallback(() => {
				if (!scrollElement) throw Error('scrollElement must be defined');
				return scrollElement;
			}, [scrollElement]);

			return isLoading ? (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				<div style={{ width: '100%', height: 800 }}>
					<IssueHierarchySkeleton
						ariaLabel={intl.formatMessage(commonMessages.loadingIssuesMessage)}
						numOfIssues={11}
						sidePadding={16}
						showIssueSelectCheckbox
						showIssueWithoutParent
					/>
				</div>
			) : (
				<Box onMouseDown={onListContainerMouseDown} ref={ref}>
					{(() => {
						if (issues.length === 0) {
							if (noIssuesInPlan) {
								return <NoIssuesEmptyState isReadOnly={isReadOnly} />;
							}
							return <NoIssuesInFilterView />;
						}
						if (scrollElement) {
							return (
								<div
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									className={`${styles['drop-container']} ${getDropClassName(issues, drop)}`}
									data-testid="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.scope.issues"
									data-name="ScopeList"
								>
									<SortableList
										distance={3}
										getContainer={getScrollContainer}
										isReadOnly={isReadOnly}
										lockToContainerEdges
										lockOffset="250%"
										onSortEnd={onSortEnd}
										onSortOver={onSortOver}
										onSortStart={onSortStart}
										rowRenderer={rowRenderer}
										rows={issues}
										scrollElement={scrollElement}
										scrollToIndex={scrollToIndex}
										heightOffset={calculateHeightOffset()}
										shouldCancelStart={shouldCancelStart(disableDnD)}
										width={width}
										dropParentId={parentId}
										StickyHeader={StickyHeaderComponent}
									/>
								</div>
							);
						}
						return null;
					})()}
				</Box>
			);
		},
	),
	(_, nextProps) => nextProps.isDragging ?? false,
);

export default IssuesHOC;
